import { useCallback, useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { useIssuesStore } from "@/stores/pm/hooks/usePMStore";
import { subscribeToIssueDependencies } from "@/stores/pm/repository";

interface LiveUpdatesOptions {
  /** False until the page knows it can load issues (org context, projects). */
  enabled: boolean;
  /** Re-read the page's issues. */
  reload: () => void;
  /** Anything else the page refreshes when the chatbot writes (mention dots). */
  onChatbotWrite?: () => void;
}

/**
 * Keep an issues page current without a refresh, whoever changes the data:
 * a colleague (realtime), the chatbot (its `franklin:pm-issue-changed` window
 * event), or a schedule cascade the page's own edit set off.
 *
 * What moves with it:
 *  - the issue rows, and the open issue beside them (`loadIssues` refreshes
 *    the selection too);
 *  - the dependency arrows and the panel's dependency list, which realtime on
 *    `pm_issue_dependencies` now reaches;
 *  - the shared row order, which a chatbot write can change by moving an issue
 *    into another band.
 *
 * A change that arrives while a row or bar is being dragged is held, not
 * dropped: reloading mid-gesture would swap the rows out from under the
 * pointer, but ignoring it would leave the page stale until the next change
 * happened to arrive. The held reload runs as the drag ends.
 *
 * @returns the callback the Gantt and the board report their drags through.
 */
export function usePMIssueLiveUpdates(buildingId: number, options: LiveUpdatesOptions) {
  const issuesStore = useIssuesStore();
  const queryClient = useQueryClient();
  const dragActive = useRef(false);
  const missedWhileDragging = useRef(false);
  // Read through a ref, so a new function identity each render neither
  // resubscribes nor leaves the subscription calling a stale closure.
  const latest = useRef(options);
  latest.current = options;

  const reloadIssues = useCallback(() => {
    if (dragActive.current) {
      missedWhileDragging.current = true;
      return;
    }
    latest.current.reload();
  }, []);

  const refreshDependencies = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ["pm-issue-dependencies", buildingId] });
  }, [buildingId, queryClient]);

  useEffect(() => {
    if (!options.enabled || !Number.isFinite(buildingId)) return;

    issuesStore.subscribe(buildingId, reloadIssues);
    const dependencyChannel = subscribeToIssueDependencies(refreshDependencies);

    // The chatbot announces its own writes: realtime does not reliably carry a
    // write made under the service's database role, and the announcement also
    // arrives before any realtime echo would.
    const onChatbotWrite = (e: Event) => {
      const detail = (e as CustomEvent<{ buildingId?: number }>).detail;
      if (detail?.buildingId != null && detail.buildingId !== buildingId) return;
      reloadIssues();
      refreshDependencies();
      void queryClient.invalidateQueries({ queryKey: ["pm-issue-placements", buildingId] });
      latest.current.onChatbotWrite?.();
    };
    window.addEventListener("franklin:pm-issue-changed", onChatbotWrite);

    return () => {
      issuesStore.unsubscribe();
      dependencyChannel.unsubscribe();
      window.removeEventListener("franklin:pm-issue-changed", onChatbotWrite);
    };
  }, [buildingId, options.enabled, issuesStore, queryClient, refreshDependencies, reloadIssues]);

  return useCallback((active: boolean) => {
    dragActive.current = active;
    if (!active && missedWhileDragging.current) {
      missedWhileDragging.current = false;
      latest.current.reload();
    }
  }, []);
}
